// AssetStepperForm.jsx
import React, { useEffect, useState } from "react";
import { Stepper, Step, StepLabel } from "@mui/material";
import FileUpload from "../../Constant/fileupload";
import styles from "../../../assets/scss/core/utilites/assetStepperForm.module.scss";
import { CustomTooltip } from "../../../components/CustomTooltip";
import { FaInfo } from "react-icons/fa";
import Select from "react-select";
import { searchcategoryOptionApi } from "../../../services/SearchService";
const steps = ["Basic Info", "Warranty & Service", "Insurance"];

const SLMDynamicInfo = ({ cost, residual, months }) => {
  const totalCost = cost || 100000;
  const totalResidual = residual || 10000;
  const totalMonths = months || 60;

  const depreciableAmount = totalCost - totalResidual;
  const monthlyDepreciation = depreciableAmount / totalMonths;
  const yearlyDepreciation = monthlyDepreciation * 12;

  return (
    <div>
      <h5 className="text-white" style={{ marginBottom: "0.3rem" }}>
        SLM (Straight Line Method)
      </h5>

      <p>
        Depreciates an asset by an <strong>equal fixed amount</strong> each
        month, making it simple and predictable.
      </p>

      <p>
        <strong>Formula:</strong>
        <br />
        <code>(Cost of Asset – Residual Value) ÷ Useful Life (in months)</code>
      </p>

      <hr style={{ margin: "0.5rem 0" }} />

      <p>
        <strong>Asset Cost:</strong> ₹{totalCost.toLocaleString()} <br />
        <strong>Residual Value:</strong> ₹{totalResidual.toLocaleString()}{" "}
        <br />
        <strong>Useful Life:</strong> {totalMonths} months (
        {(totalMonths / 12).toFixed(1)} years)
      </p>

      <hr style={{ margin: "0.5rem 0" }} />

      <h5 className="text-white" style={{ marginBottom: "0.3rem" }}>
        Depreciation:
      </h5>
      <ul style={{ paddingLeft: "0.4rem", margin: 0 }}>
        <li>
          <strong>Monthly:</strong> ₹
          {Math.round(monthlyDepreciation).toLocaleString()}
        </li>
        <li>
          <strong>Yearly:</strong> ₹
          {Math.round(yearlyDepreciation).toLocaleString()}
        </li>
        <li>
          <strong>Total Depreciable:</strong> ₹
          {depreciableAmount.toLocaleString()}
        </li>
      </ul>
    </div>
  );
};

const RBMDynamicInfo = ({ cost, annualRate, scrapValue = 1000 }) => {
  const initialCost = cost || 100000;
  const annualDepRate = annualRate || 20;
  const monthlyRate = annualDepRate / 100 / 12;

  let bookValue = initialCost;
  let schedule = [];
  let month = 1;

  const MAX_MONTHS = 600;

  while (month <= MAX_MONTHS) {
    const depreciation = bookValue * monthlyRate;
    const nextValue = bookValue - depreciation;

    if (nextValue < scrapValue) break;

    bookValue = nextValue;
    schedule.push({
      month,
      depreciation: Math.round(depreciation),
      remaining: Math.round(bookValue),
    });

    month++;
  }

  const totalMonths = schedule.length;
  const totalDepreciated = initialCost - bookValue;

  const firstTwo = schedule.slice(0, 2);
  const lastTwo = schedule.slice(-2);

  return (
    <div style={{ maxWidth: 240, fontSize: "0.8rem", lineHeight: "1.4" }}>
      <strong>RBM (Reducing Balance Method)</strong>
      <p style={{ margin: "0.3rem 0" }}>
        Depreciates on <strong>remaining value</strong> → higher in early
        months, lower later.
      </p>

      <p>
        <strong>Formula:</strong>
        <br />
        <code>Book Value × Monthly Depreciation Rate</code>
      </p>
      <hr style={{ margin: "0.5rem 0" }} />
      <p style={{ margin: "0.3rem 0" }}>
        <strong>Asset:</strong> ₹{initialCost.toLocaleString()} <br />
        <strong>Rate:</strong> {annualDepRate}%/year <br />
        <strong>Scrap:</strong> ₹{scrapValue.toLocaleString()}
      </p>

      {schedule.length > 0 ? (
        <>
          <p style={{ margin: "0.3rem 0" }}>
            <strong>Schedule:</strong>
          </p>
          <ul style={{ paddingLeft: "1rem", margin: "0.3rem 0" }}>
            {firstTwo.map((m) => (
              <li key={m.month}>
                M{m.month}: ₹{m.depreciation.toLocaleString()} → ₹
                {m.remaining.toLocaleString()}
              </li>
            ))}

            {totalMonths > 4 && <li>… {totalMonths - 4} months …</li>}

            {lastTwo.map((m) => (
              <li key={m.month}>
                M{m.month}: ₹{m.depreciation.toLocaleString()} → ₹
                {m.remaining.toLocaleString()}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p style={{ margin: "0.3rem 0" }}>⚠️ Too low to reach scrap value</p>
      )}

      <p style={{ margin: "0.3rem 0" }}>
        <strong>Life:</strong> {totalMonths} months <br />
        <strong>Depreciated:</strong> ₹{totalDepreciated.toLocaleString()}{" "}
        <br />
        <strong>Final Value:</strong> ₹{bookValue.toLocaleString()}
      </p>
    </div>
  );
};

const AssetStepperForm = ({
  initialValues = {},
  onSubmit,
  onCancel,
  isEditMode,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  //catagory
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [filteredSubCategoryOptions, setFilteredSubCategoryOptions] = useState(
    []
  );
  useEffect(() => {
    setForm({
      assetName: "",
      catagory: null,
      sub_catagory: null,
      other_info: "",
      purchaseCost: "",
      purchaseDate: "",
      assetId: "",
      manufacturer: "",
      brand: "",
      modelNumber: "",
      serialNumber: "",
      warrantyEnd: "",
      warrantyFile: null,
      serviceCycle: "",
      lifespan: "",
      depreciationRate: "",
      depreciationCycle: "",
      scrapValue: "",
      insuranceProvider: "",
      policyNumber: "",
      insuranceExpiry: "",
      depreciation_method: "",
      insuranceFile: null,
      image: null,
      ...initialValues,
    });
    setActiveStep(0);
    setErrors({});
    if (initialValues?.catagory && initialValues?.sub_catagory) {
      setSelectedCategory(initialValues?.catagory);
      setSelectedSubCategory(initialValues?.sub_catagory);
      const filtered = subCategoryOptions.filter(
        (sub) => sub.categoryId === initialValues?.catagory.value
      );
      setFilteredSubCategoryOptions(filtered);
    } else {
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setFilteredSubCategoryOptions(subCategoryOptions);
    }
  }, [initialValues]);
  const handleRresetAsset = () => {
    setForm({
      assetName: "",
      catagory: null,
      sub_catagory: null,
      other_info: "",
      purchaseCost: "",
      purchaseDate: "",
      assetId: "",
      manufacturer: "",
      brand: "",
      modelNumber: "",
      serialNumber: "",
      warrantyEnd: "",
      warrantyFile: null,
      serviceCycle: "",
      lifespan: "",
      depreciationRate: "",
      scrapValue: "",
      insuranceProvider: "",
      policyNumber: "",
      insuranceExpiry: "",
      depreciation_method: "",
      insuranceFile: null,
      image: null,
    });
    setActiveStep(0);
    setErrors({});
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setFilteredSubCategoryOptions(subCategoryOptions);
  };
  useEffect(() => {
    searchcategoryOptionApi()
      .then((res) => {
        if (res?.data?.results) {
          const categoryOpts = res.data.results.map((item) => ({
            label: item.name,
            value: item.id,
            subcategories: item.subcategories || [],
          }));
          setCategoryOptions(categoryOpts);

          const allSubCats = categoryOpts.flatMap((cat) =>
            cat.subcategories.map((sub) => ({
              label: sub.name,
              value: sub.id,
              categoryId: cat.value,
            }))
          );

          setSubCategoryOptions(allSubCats);
          setFilteredSubCategoryOptions(allSubCats);
        }
      })
      .catch((err) => console.error("Category fetch failed:", err));
  }, []);

  const handleInput = (e) => {
    const { name, value, type, files } = e.target;

    if ((name === "scrapValue" || name === "lifespan") && !value) {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        depreciation_method: "",
        depreciationRate: "",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === "file" ? files[0] : value,
      }));
    }

    if (value && errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const handleNext = () => {
    const newErrors = {};

    if (activeStep === 0) {
      if (!form.assetName) newErrors.assetName = true;
      if (!form.purchaseCost) newErrors.purchaseCost = true;
      if (!form.purchaseDate) newErrors.purchaseDate = true;
      if (!form.catagory) newErrors.catagory = true;
      if (!form.sub_catagory) newErrors.sub_catagory = true;
    }

    if (activeStep === 1) {
      const hasWarranty =
        form.serviceCycle || form.warrantyEnd;
      if (hasWarranty) {
        if (!form.serviceCycle) newErrors.serviceCycle = true;

        if (!form.warrantyEnd) newErrors.warrantyEnd = true;
      }
      const hasDepreciation =
        // form.depreciationRate ||
        form.scrapValue || form.lifespan;
      if (hasDepreciation) {
        if (!form.lifespan) newErrors.lifespan = true;
        // if (!form.depreciationRate) newErrors.depreciationRate = true;
        if (!form.scrapValue) newErrors.scrapValue = true;
      }


      if(form.depreciation_method=="RBM"){
        if(!form.depreciationRate) newErrors.depreciationRate = true;
      }
    }

    if (activeStep === 2) {
      const hasInsurance =
        form.insuranceProvider ||
        form.policyNumber ||
        form.insuranceExpiry ||
        form.insuranceFile;
      if (hasInsurance) {
        if (!form.insuranceProvider) newErrors.insuranceProvider = true;
        if (!form.policyNumber) newErrors.policyNumber = true;
        if (!form.insuranceExpiry) newErrors.insuranceExpiry = true;
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      if (activeStep === steps.length - 1) {
        onSubmit(form);
        setActiveStep(0);
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setErrors({});
    setActiveStep((prev) => prev - 1);
  };

  const handleCategoryChange = (selected) => {
    if (selected?.value) {
      setErrors({ ...errors, catagory: false });
    }
    setSelectedCategory(selected);
    setSelectedSubCategory(null);
    setForm({ ...form, catagory: selected || null, sub_catagory: null });
    const filtered = subCategoryOptions.filter(
      (sub) => sub.categoryId === selected.value
    );
    setFilteredSubCategoryOptions(filtered);
  };

  const handleSubCategoryChange = (selected) => {
    if (selected?.value) {
      setErrors({ ...errors, sub_catagory: false });
    }
    setSelectedSubCategory(selected);
    setForm({ ...form, sub_catagory: selected || null });

    if (!selectedCategory || selected.categoryId !== selectedCategory.value) {
      const relatedCat = categoryOptions.find(
        (cat) => cat.value === selected.categoryId
      );
      setSelectedCategory(relatedCat);
      setForm({ ...form, catagory: relatedCat || null });

      const filtered = subCategoryOptions.filter(
        (sub) => sub.categoryId === relatedCat?.value
      );
      setFilteredSubCategoryOptions(filtered);
    }
  };
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <div className="row gy-2">
            <div className="col-md-4">
              <label className="form-label text-dark">Asset Name</label>
              <span className="text-danger fw-medium ms-1">*</span>
              <input
                name="assetName"
                className="form-control"
                value={form.assetName}
                onChange={handleInput}
                placeholder="e.g. Asus Zen Book"
              />
              {errors?.assetName && (
                <div className="text-danger" style={{ fontSize: "0.8rem" }}>
                  Mandatory Field
                </div>
              )}
            </div>{" "}
            <div className="col-md-4">
              <label className="form-label text-dark">Catagory</label>
              <span className="text-danger fw-medium ms-1">*</span>
              <Select
                options={categoryOptions}
                classNamePrefix="select"
                className="react-select"
                placeholder="Electronics"
                onChange={handleCategoryChange}
                value={selectedCategory}
              />
              {errors?.catagory && (
                <div className="text-danger" style={{ fontSize: "0.8rem" }}>
                  Please select catagory
                </div>
              )}
            </div>{" "}
            <div className="col-md-4">
              <label className="form-label text-dark">Sub-Category</label>
              <span className="text-danger fw-medium ms-1">*</span>
              <Select
                options={filteredSubCategoryOptions}
                classNamePrefix="select"
                className="react-select"
                placeholder="Laptop"
                onChange={handleSubCategoryChange}
                value={selectedSubCategory}
              />
              {errors?.sub_catagory && (
                <div className="text-danger" style={{ fontSize: "0.8rem" }}>
                  Please select a Subcategory
                </div>
              )}
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Purchase Date</label>
              <span className="text-danger fw-medium ms-1">*</span>
              <input
                name="purchaseDate"
                type="date"
                className={`form-control ${
                  errors.purchaseDate ? "is-invalid" : ""
                }`}
                value={form.purchaseDate}
                onChange={handleInput}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Purchase Cost</label>
              <span className="text-danger fw-medium ms-1">*</span>
              <input
                name="purchaseCost"
                type="text"
                className={`form-control ${
                  errors.purchaseCost ? "is-invalid" : ""
                }`}
                value={form.purchaseCost}
                placeholder="e.g 30000"
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleInput(e);
                  }
                }}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Manual Asset ID</label>
              <CustomTooltip
                title="Manual Asset ID is the pre-existing code physically tagged on the asset (e.g., barcode, RFID tag, serial tag). Ensure it’s unique and correctly entered."
                arrow
                placement="right"
              >
                <span
                  className="rounded-1 ms-1 btn btn-sm btn-outline-light"
                  style={{ cursor: "pointer", padding: "0.2rem 0.2rem" }}
                >
                  <FaInfo size={9} className="text-dark" />
                </span>
              </CustomTooltip>
              <input
                name="assetId"
                type="text"
                className="form-control"
                value={form.assetId}
                onChange={handleInput}
                placeholder="e.g. AST-00123"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Manufacturer</label>
              <input
                name="manufacturer"
                className="form-control"
                value={form.manufacturer}
                onChange={handleInput}
                placeholder="e.g. Hewlett-Packard"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Brand</label>
              <input
                name="brand"
                className="form-control"
                value={form.brand}
                onChange={handleInput}
                placeholder="e.g. HP"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Model Number</label>
              <input
                name="modelNumber"
                className="form-control"
                value={form.modelNumber}
                onChange={handleInput}
                placeholder="e.g. 840-G6"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Serial Number</label>
              <input
                name="serialNumber"
                className="form-control"
                value={form.serialNumber}
                onChange={handleInput}
                placeholder="e.g. SN-1234567890"
              />
            </div>
            <div className="col-md-8">
              <label className="form-label text-dark">Other Information</label>
              <input
                name="other_info"
                className="form-control"
                value={form.other_info}
                onChange={handleInput}
                placeholder="Type other info of this asset"
              />
            </div>
          </div>
        );

      case 1:
        return (
          <>
            <div className="row gy-2">
              <div className="col-md-4">
                <label className="form-label text-dark">
                  Service Cycle (months)
                </label>
                <div className="position-relative overflow-hidden">
                  <input
                    name="serviceCycle"
                    type="text"
                    pattern="[0-9]*"
                    placeholder="e.g. 12"
                    className={`form-control ${
                      errors.serviceCycle ? "is-invalid" : ""
                    }`}
                    value={form.serviceCycle}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        handleInput(e);
                      }
                    }}
                  />
                  {form.serviceCycle && parseInt(form.serviceCycle) > 0 && (
                    <div
                      className="position-absolute top-50 translate-middle-y small px-2 py-1 rounded"
                      style={{
                        right: "0px",
                        fontSize: "0.8rem",
                        lineHeight: "1",
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>
                        {(parseInt(form.serviceCycle) / 12)
                          .toFixed(1)
                          .replace(/\.0$/, "")}{" "}
                        year{parseInt(form.serviceCycle) >= 24 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-dark">
                  Warranty End Date
                </label>
                <input
                  name="warrantyEnd"
                  type="date"
                  className={`form-control ${
                    errors.warrantyEnd ? "is-invalid" : ""
                  }`}
                  value={form.warrantyEnd}
                  onChange={handleInput}
                  min={
                    new Date(Date.now() + 86400000).toISOString().split("T")[0]
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label text-dark">
                  Lifespan (months)
                </label>
                <div className="position-relative">
                  <input
                    name="lifespan"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="e.g. 12"
                    className={`form-control ${
                      errors.lifespan ? "is-invalid" : ""
                    }`}
                    value={form.lifespan}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        handleInput(e);
                      }
                    }}
                  />
                  {form.lifespan && parseInt(form.lifespan) > 0 && (
                    <div
                      className="position-absolute top-50 translate-middle-y small px-2 py-1 rounded"
                      style={{
                        right: "0px",
                        fontSize: "0.8rem",
                        lineHeight: "1",
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>
                        {(parseInt(form.lifespan) / 12)
                          .toFixed(1)
                          .replace(/\.0$/, "")}{" "}
                        year{parseInt(form.lifespan) >= 24 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label text-dark">Scrap Value</label>
                <input
                  name="scrapValue"
                  type="text"
                  placeholder="e.g. 5000"
                  className={`form-control ${
                    errors.scrapValue ? "is-invalid" : ""
                  }`}
                  value={form.scrapValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      handleInput(e);
                    }
                  }}
                />
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-md-4">
                <span className="form-label text-dark">
                  Depreciation Method
                </span>
                <div className="d-flex gap-2 align-items-center mt-2">
                  <div className="form-check form-check-inline p-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="depreciation_method"
                      value="SLM"
                      id="slm"
                      disabled={!(form.scrapValue && form.lifespan)}
                      checked={form.depreciation_method === "SLM"}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          depreciation_method: e.target.value,
                          depreciationRate: "",
                        }))
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    <label
                      className="form-check-label text-dark fw-medium"
                      htmlFor="slm"
                      style={{ fontSize: "0.9rem",paddingLeft:'0.3rem' }}
                    >
                      SLM{" "}
                    </label>
                    <CustomTooltip
                      title={
                        <SLMDynamicInfo
                          cost={form.purchaseCost}
                          residual={form.scrapValue}
                          months={form.lifespan}
                        />
                      }
                      arrow
                      placement="top"
                    >
                      {" "}
                      <span
                        className="rounded-1 ms-1 btn btn-sm btn-outline-light"
                        style={{
                          cursor: "pointer",
                          padding: "0.2rem 0.2rem",
                        }}
                      >
                        <FaInfo size={9} className="text-dark" />
                      </span>
                    </CustomTooltip>
                  </div>

                  <div className="form-check form-check-inline p-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="depreciation_method"
                      value="RBM"
                      id="rbm"
                      disabled={!(form.scrapValue && form.lifespan)}
                      checked={form.depreciation_method === "RBM"}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          depreciation_method: e.target.value,
                          depreciationRate: "",
                        }))
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                      }}
                    />
                    <label
                      className="form-check-label text-dark fw-medium"
                      htmlFor="rbm"
                      style={{ fontSize: "0.9rem",paddingLeft:'0.3rem' }}
                    >
                      RBM{" "}
                    </label>
                    <CustomTooltip
                      title={
                        <RBMDynamicInfo
                          scrapValue={form.scrapValue}
                          cost={form.purchaseCost}
                          annualRate={form.depreciationRate}
                        />
                      }
                      arrow
                      placement="top"
                    >
                      {" "}
                      <span
                        className="rounded-1 ms-1 btn btn-sm btn-outline-light"
                        style={{
                          cursor: "pointer",
                          padding: "0.2rem 0.2rem",
                        }}
                      >
                        <FaInfo size={9} className="text-dark" />
                      </span>
                    </CustomTooltip>
                  </div>
                </div>
              </div>

              {form.depreciation_method == "RBM" && (
                <div className="col-md-4">
                  <label className="form-label text-dark">
                    Depreciation Rate (per year)
                  </label>
                  <input
                    name="depreciationRate"
                    type="text"
                    placeholder="e.g. 15%"
                    className={`form-control ${
                      errors.depreciationRate ? "is-invalid" : ""
                    }`}
                    value={form.depreciationRate}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        handleInput(e);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </>
        );

      case 2:
        return (
          <div className="row">
            <div className="col-md-4">
              <label className="form-label text-dark">Insurance Provider</label>
              <input
                name="insuranceProvider"
                placeholder="e.g. ICICI Lombard"
                className={`form-control ${
                  errors.insuranceProvider ? "is-invalid" : ""
                }`}
                value={form.insuranceProvider}
                onChange={handleInput}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Policy Number</label>
              <input
                name="policyNumber"
                className={`form-control ${
                  errors.policyNumber ? "is-invalid" : ""
                }`}
                value={form.policyNumber}
                onChange={handleInput}
                placeholder="e.g. POL-123456789"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label text-dark">Expiry Date</label>
              <input
                name="insuranceExpiry"
                type="date"
                className={`form-control ${
                  errors.insuranceExpiry ? "is-invalid" : ""
                }`}
                value={form.insuranceExpiry}
                onChange={handleInput}
                placeholder="Select insurance expiry date"
                min={
                  new Date(Date.now() + 86400000).toISOString().split("T")[0]
                }
              />
            </div>
            <div className="col-md-4 mt-3 mb-3">
              <label className="form-label text-dark">Insurance File</label>
              <FileUpload
                label="Drag & drop File (max size 3MB)"
                accept=".pdf"
                multiple={false}
                value={form.insuranceFile}
                onChange={(files) =>
                  setForm((prev) => ({ ...prev, insuranceFile: files }))
                }
              />
            </div>
            <div className="col-md-4 mt-3 mb-3">
              <label className="form-label text-dark">Asset Image</label>
              <FileUpload
                label="Drag & drop File (max size 3MB)"
                accept=".jpg,.jpeg,.png"
                multiple={false}
                value={form.image}
                onChange={(files) =>
                  setForm((prev) => ({ ...prev, image: files }))
                }
              />
            </div>
            <div className="col-md-4 mt-3 mb-3">
              <label className="form-label text-dark">Warranty File</label>
              <FileUpload
                label="Drag & drop File (max size 3MB)"
                accept=".pdf"
                multiple={false}
                value={form.warrantyFile}
                onChange={(files) => {
                  setForm((prev) => ({ ...prev, warrantyFile: files }));
                }}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="card">
      <div className="p-3 border-bottom">
        <h5>{isEditMode ? "Edit" : "Add"} Asset</h5>
      </div>
      <div className={`card-body ${isEditMode ? "text-bg-warning-soft" : ""}`}>
        <div className={styles.assetStepper}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, i) => (
              <Step key={i}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
        {renderStep()}

        <div className="d-flex justify-content-between gap-2 mt-5">
          <button
            className="btn btn-danger rounded-2"
            style={{ padding: "0.4rem 1.1rem" }}
            onClick={() => {
              handleRresetAsset();
              onCancel();
            }}
          >
            Reset
          </button>
          <div className="d-flex gap-2">
            {activeStep > 0 && (
              <button
                className="btn  rounded-2 stylish-transparent-btn"
                style={{ padding: "0.4rem 1.1rem" }}
                onClick={handleBack}
              >
                Back
              </button>
            )}
            <button
              className="btn rounded-2 btn-primary"
              style={{ padding: "0.4rem 1.1rem" }}
              onClick={handleNext}
            >
              {activeStep === steps.length - 1 ? "Save Asset" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetStepperForm;
